import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Bug,
  Scan,
  Sparkles,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Clock,
  Trash2,
  Loader2,
  Send,
  RefreshCw,
  Eye,
  EyeOff,
  Code,
  Palette,
  Database,
  Settings,
  ChevronDown,
  ChevronRight,
  Zap,
  History,
  Play,
  RotateCcw,
  FileCode,
  Brain,
  Calendar,
  Mail,
  TestTube,
  GitBranch,
  Lightbulb,
  TrendingUp,
  Shield,
  Check,
  X,
  Upload,
  Image,
  ExternalLink,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle, label: 'Critical' },
  high: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertTriangle, label: 'High' },
  medium: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, label: 'Medium' },
  low: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Eye, label: 'Low' }
};

const CATEGORY_CONFIG = {
  visual: { color: 'bg-purple-500/20 text-purple-400', icon: Palette, label: 'Visual/UI' },
  code: { color: 'bg-cyan-500/20 text-cyan-400', icon: Code, label: 'Code' },
  build: { color: 'bg-green-500/20 text-green-400', icon: Settings, label: 'Build' },
  data: { color: 'bg-pink-500/20 text-pink-400', icon: Database, label: 'Data' },
  performance: { color: 'bg-amber-500/20 text-amber-400', icon: Zap, label: 'Performance' }
};

const STATUS_CONFIG = {
  open: { color: 'bg-red-500/20 text-red-400', label: 'Open' },
  in_progress: { color: 'bg-yellow-500/20 text-yellow-400', label: 'In Progress' },
  fixed: { color: 'bg-green-500/20 text-green-400', label: 'Fixed' },
  ignored: { color: 'bg-gray-500/20 text-gray-400', label: 'Ignored' }
};

export default function BugScanner() {
  const [activeTab, setActiveTab] = useState('issues');
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const [severityCounts, setSeverityCounts] = useState({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('open');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  
  // AI Command
  const [aiCommand, setAiCommand] = useState('');
  const [aiContext, setAiContext] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [previewOnly, setPreviewOnly] = useState(true);
  
  // Testing
  const [testLoading, setTestLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  
  // Deployments
  const [deployments, setDeployments] = useState([]);
  const [rollbackLoading, setRollbackLoading] = useState(null);
  
  // Previews
  const [previews, setPreviews] = useState([]);
  const [applyingPreview, setApplyingPreview] = useState(null);
  
  // Learning
  const [insights, setInsights] = useState([]);
  const [insightStats, setInsightStats] = useState({});
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  
  // Schedule
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  
  // Command History
  const [commandHistory, setCommandHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Bug Details Modal
  const [selectedBug, setSelectedBug] = useState(null);
  const [showBugModal, setShowBugModal] = useState(false);
  const [fixingBug, setFixingBug] = useState(null);
  
  // Report Bug Modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [newBug, setNewBug] = useState({
    category: 'visual',
    severity: 'medium',
    title: '',
    description: '',
    location: ''
  });

  // Screenshot Upload
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotDescription, setScreenshotDescription] = useState('');
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [screenshotAnalysis, setScreenshotAnalysis] = useState(null);
  const fileInputRef = useRef(null);

  // Deploy
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);

  // Fix All
  const [fixingAll, setFixingAll] = useState(false);
  const [fixAllResult, setFixAllResult] = useState(null);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchIssues = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/bug-scanner/issues?limit=100`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;
      if (severityFilter !== 'all') url += `&severity=${severityFilter}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      
      if (response.ok) {
        const data = await response.json();
        setIssues(data.issues || []);
        setStatusCounts(data.status_counts || {});
        setSeverityCounts(data.severity_counts || {});
      }
    } catch (err) {
      console.error('Failed to fetch issues:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, severityFilter]);

  const fetchCommandHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/history?limit=10`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCommandHistory(data.commands || []);
      }
    } catch (err) {
      console.error('Failed to fetch command history:', err);
    }
  };

  const fetchTestHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/test/history?limit=10`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTestHistory(data.tests || []);
      }
    } catch (err) {
      console.error('Failed to fetch test history:', err);
    }
  };

  const fetchDeployments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/deployments?limit=20`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDeployments(data.deployments || []);
      }
    } catch (err) {
      console.error('Failed to fetch deployments:', err);
    }
  };

  const fetchPreviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/previews?status=pending`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPreviews(data.previews || []);
      }
    } catch (err) {
      console.error('Failed to fetch previews:', err);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/learning/insights?limit=50`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights || []);
        setInsightStats(data.stats || {});
      }
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    }
  };

  const fetchScheduleConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/schedule`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setScheduleConfig(data);
        setEmailRecipients(data.email_recipients?.join(', ') || '');
      }
    } catch (err) {
      console.error('Failed to fetch schedule config:', err);
    }
  };

  useEffect(() => {
    fetchIssues();
    fetchCommandHistory();
    fetchTestHistory();
    fetchDeployments();
    fetchPreviews();
    fetchInsights();
    fetchScheduleConfig();
  }, [fetchIssues]);

  const runScan = async (scanType = 'full') => {
    setScanning(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/scan`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ scan_type: scanType })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Scan complete: ${data.issues_found} issues found, ${data.new_issues} new`);
        fetchIssues();
        fetchInsights();
      } else {
        toast.error('Scan failed');
      }
    } catch (err) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const executeAiCommand = async () => {
    if (!aiCommand.trim()) {
      toast.error('Please enter a command');
      return;
    }
    
    setAiLoading(true);
    setAiResponse(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/ai-command`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          command: aiCommand,
          context: aiContext || undefined,
          bug_id: selectedBug?.bug_id,
          preview_only: previewOnly
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.response);
        toast.success(previewOnly ? 'Preview generated' : 'AI command executed');
        fetchCommandHistory();
        fetchPreviews();
      } else {
        toast.error('Command failed');
      }
    } catch (err) {
      toast.error('Command failed');
    } finally {
      setAiLoading(false);
    }
  };

  const runTests = async (testType = 'all') => {
    setTestLoading(true);
    setTestResults(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/test/run`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ test_type: testType })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
        toast.success(`Tests ${data.overall_status === 'passed' ? 'passed' : 'completed with issues'}`);
        fetchTestHistory();
      } else {
        toast.error('Tests failed to run');
      }
    } catch (err) {
      toast.error('Tests failed to run');
    } finally {
      setTestLoading(false);
    }
  };

  const fixTestFailures = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/test/fix?test_id=${testId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setTestResults(prev => ({ ...prev, fix_suggestion: data.fix_suggestion }));
        toast.success('Fix suggestions generated');
      } else {
        toast.error('Failed to generate fixes');
      }
    } catch (err) {
      toast.error('Failed to generate fixes');
    }
  };

  const uploadScreenshot = async () => {
    if (!screenshotFile) {
      toast.error('Please select a screenshot');
      return;
    }
    
    setUploadingScreenshot(true);
    setScreenshotAnalysis(null);
    
    try {
      const formData = new FormData();
      formData.append('file', screenshotFile);
      formData.append('description', screenshotDescription);
      if (selectedBug?.bug_id) {
        formData.append('bug_id', selectedBug.bug_id);
      }
      
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/upload-screenshot`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setScreenshotAnalysis(data.ai_analysis);
        toast.success('Screenshot analyzed');
        setScreenshotFile(null);
        setScreenshotDescription('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const deployChanges = async (options = {}) => {
    setDeploying(true);
    setDeployResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/deploy`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          command_id: options.commandId,
          preview_id: options.previewId,
          bug_id: options.bugId || selectedBug?.bug_id,
          description: options.description || aiCommand,
          apply_fix: options.applyFix || false
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeployResult(data);
        toast.success('Deploy initiated');
        fetchDeployments();
      } else {
        toast.error('Deploy failed');
      }
    } catch (err) {
      toast.error('Deploy failed');
    } finally {
      setDeploying(false);
    }
  };

  const fixAllIssues = async () => {
    setFixingAll(true);
    setFixAllResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/scan/fix-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setFixAllResult(data);
        toast.success(`Generated fixes for ${data.fixes?.length || 0} issues`);
        fetchIssues();
      } else {
        toast.error('Failed to generate fixes');
      }
    } catch (err) {
      toast.error('Failed to generate fixes');
    } finally {
      setFixingAll(false);
    }
  };

  const rollbackDeployment = async (deploymentId) => {
    setRollbackLoading(deploymentId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/rollback/${deploymentId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Rollback completed successfully');
        fetchDeployments();
      } else {
        toast.error('Rollback failed');
      }
    } catch (err) {
      toast.error('Rollback failed');
    } finally {
      setRollbackLoading(null);
    }
  };

  const applyPreview = async (previewId) => {
    setApplyingPreview(previewId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/apply-change/${previewId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Change applied successfully');
        fetchPreviews();
        fetchDeployments();
      } else {
        toast.error('Failed to apply change');
      }
    } catch (err) {
      toast.error('Failed to apply change');
    } finally {
      setApplyingPreview(null);
    }
  };

  const rejectPreview = async (previewId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/reject-change/${previewId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Change rejected');
        fetchPreviews();
      }
    } catch (err) {
      toast.error('Failed to reject change');
    }
  };

  const getSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/learning/suggest`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions);
        toast.success('AI suggestions generated');
      }
    } catch (err) {
      toast.error('Failed to get suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const updateSchedule = async () => {
    setScheduleLoading(true);
    try {
      const recipients = emailRecipients.split(',').map(e => e.trim()).filter(e => e);
      
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/schedule`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          enabled: scheduleConfig?.enabled || false,
          frequency: scheduleConfig?.frequency || 'daily',
          scan_type: scheduleConfig?.scan_type || 'full',
          email_report: scheduleConfig?.email_report || false,
          email_recipients: recipients
        })
      });
      
      if (response.ok) {
        toast.success('Schedule updated');
        fetchScheduleConfig();
      }
    } catch (err) {
      toast.error('Failed to update schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const runScheduledScanNow = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/schedule/run-now`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Scheduled scan triggered');
      }
    } catch (err) {
      toast.error('Failed to trigger scan');
    }
  };

  const autoFixBug = async (bugId) => {
    setFixingBug(bugId);
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/fix/${bugId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Fix analysis complete');
        setSelectedBug(prev => prev ? { ...prev, suggested_fix: data.fix_suggestion, status: 'in_progress' } : null);
        fetchIssues();
        fetchInsights();
      } else {
        toast.error('Auto-fix failed');
      }
    } catch (err) {
      toast.error('Auto-fix failed');
    } finally {
      setFixingBug(null);
    }
  };

  const updateBugStatus = async (bugId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/status/${bugId}?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success(`Status updated to ${status}`);
        fetchIssues();
        fetchInsights();
        if (selectedBug?.bug_id === bugId) {
          setSelectedBug(prev => ({ ...prev, status }));
        }
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteBug = async (bugId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/issue/${bugId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Issue deleted');
        setShowBugModal(false);
        setSelectedBug(null);
        fetchIssues();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const reportBug = async () => {
    if (!newBug.title.trim() || !newBug.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/bug-scanner/report`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newBug)
      });
      
      if (response.ok) {
        toast.success('Bug reported');
        setShowReportModal(false);
        setNewBug({ category: 'visual', severity: 'medium', title: '', description: '', location: '' });
        fetchIssues();
      }
    } catch (err) {
      toast.error('Failed to report bug');
    }
  };

  const openBugDetails = (bug) => {
    setSelectedBug(bug);
    setShowBugModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bug-scanner">
      {/* Header with Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-red-500/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical</p>
                <p className="text-2xl font-bold text-red-400">{severityCounts.critical || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border-orange-500/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">High</p>
                <p className="text-2xl font-bold text-orange-400">{severityCounts.high || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-500/10 to-green-500/10 border-yellow-500/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Medium</p>
                <p className="text-2xl font-bold text-yellow-400">{severityCounts.medium || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Low</p>
                <p className="text-2xl font-bold text-blue-400">{severityCounts.low || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Fixed</p>
                <p className="text-2xl font-bold text-green-400">{statusCounts.fixed || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-6 lg:grid-cols-6">
          <TabsTrigger value="issues" className="text-xs sm:text-sm">
            <Bug className="w-4 h-4 mr-1 hidden sm:inline" />
            Issues
          </TabsTrigger>
          <TabsTrigger value="ai-command" className="text-xs sm:text-sm">
            <Sparkles className="w-4 h-4 mr-1 hidden sm:inline" />
            AI Command
          </TabsTrigger>
          <TabsTrigger value="testing" className="text-xs sm:text-sm">
            <TestTube className="w-4 h-4 mr-1 hidden sm:inline" />
            Testing
          </TabsTrigger>
          <TabsTrigger value="deployments" className="text-xs sm:text-sm">
            <GitBranch className="w-4 h-4 mr-1 hidden sm:inline" />
            Deploy
          </TabsTrigger>
          <TabsTrigger value="learning" className="text-xs sm:text-sm">
            <Brain className="w-4 h-4 mr-1 hidden sm:inline" />
            Learning
          </TabsTrigger>
          <TabsTrigger value="schedule" className="text-xs sm:text-sm">
            <Calendar className="w-4 h-4 mr-1 hidden sm:inline" />
            Schedule
          </TabsTrigger>
        </TabsList>

        {/* Issues Tab */}
        <TabsContent value="issues">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-red-400" />
                  Issue Tracker
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={() => runScan('full')}
                    disabled={scanning}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="run-scan-btn"
                  >
                    {scanning ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Scan className="w-4 h-4 mr-2" />
                    )}
                    Run Scan
                  </Button>
                  <Button 
                    onClick={fixAllIssues} 
                    disabled={fixingAll || (severityCounts.critical + severityCounts.high + severityCounts.medium + severityCounts.low) === 0}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {fixingAll ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Wrench className="w-4 h-4 mr-2" />
                    )}
                    Fix All
                  </Button>
                  <Button variant="outline" onClick={() => setShowReportModal(true)}>
                    <Bug className="w-4 h-4 mr-2" />
                    Report
                  </Button>
                  <Button variant="ghost" size="icon" onClick={fetchIssues}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Fix All Result */}
              {fixAllResult && (
                <div className="mb-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <span className="font-medium text-purple-400">
                      Generated fixes for {fixAllResult.fixes?.length || 0} issues
                    </span>
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {fixAllResult.fixes?.map((fix, idx) => (
                      <div key={idx} className="p-2 bg-background/50 rounded text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={SEVERITY_CONFIG[fix.severity]?.color}>
                            {fix.severity}
                          </Badge>
                          <span className="font-medium">{fix.title}</span>
                        </div>
                        <p className="text-muted-foreground">{fix.fix_suggestion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardHeader>
            
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="ignored">Ignored</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="visual">Visual/UI</SelectItem>
                    <SelectItem value="code">Code</SelectItem>
                    <SelectItem value="build">Build</SelectItem>
                    <SelectItem value="data">Data</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={severityFilter} onValueChange={setSeverityFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Issues List */}
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {issues.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bug className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No issues found</p>
                    <p className="text-sm">Run a scan to detect issues</p>
                  </div>
                ) : (
                  issues.map((issue) => {
                    const severityConfig = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.medium;
                    const categoryConfig = CATEGORY_CONFIG[issue.category] || CATEGORY_CONFIG.visual;
                    const statusConfig = STATUS_CONFIG[issue.status] || STATUS_CONFIG.open;
                    const SeverityIcon = severityConfig.icon;
                    const CategoryIcon = categoryConfig.icon;
                    
                    return (
                      <div
                        key={issue.bug_id}
                        className="p-4 bg-secondary/30 rounded-lg border border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                        onClick={() => openBugDetails(issue)}
                        data-testid={`bug-item-${issue.bug_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${severityConfig.color}`}>
                              <SeverityIcon className="w-4 h-4" />
                            </div>
                            <div>
                              <h4 className="font-medium">{issue.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {issue.description}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={categoryConfig.color}>
                                  <CategoryIcon className="w-3 h-3 mr-1" />
                                  {categoryConfig.label}
                                </Badge>
                                <Badge className={statusConfig.color}>
                                  {statusConfig.label}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              autoFixBug(issue.bug_id);
                            }}
                            disabled={fixingBug === issue.bug_id || issue.status === 'fixed'}
                            className="text-purple-400 hover:text-purple-300"
                          >
                            {fixingBug === issue.bug_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Zap className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Command Tab */}
        <TabsContent value="ai-command">
          <Card className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                AI Command Center
              </CardTitle>
              <CardDescription>
                Command the AI to fix bugs, edit content, improve features, or analyze code
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Command</Label>
                <Textarea
                  placeholder="Example: Fix the login button styling, Add error handling to checkout, Improve game card performance, Refactor the prediction algorithm..."
                  value={aiCommand}
                  onChange={(e) => setAiCommand(e.target.value)}
                  className="min-h-[100px] bg-background/50"
                  data-testid="ai-command-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Context (Optional)</Label>
                <Input
                  placeholder="File path, component name, or additional context..."
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  className="bg-background/50"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="preview-mode"
                    checked={previewOnly}
                    onCheckedChange={setPreviewOnly}
                  />
                  <Label htmlFor="preview-mode" className="text-sm">
                    Preview only (don't apply changes automatically)
                  </Label>
                </div>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={executeAiCommand}
                  disabled={aiLoading || !aiCommand.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="execute-ai-command-btn"
                >
                  {aiLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {previewOnly ? 'Generate Preview' : 'Execute Command'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => deployChanges({ description: aiCommand })}
                  disabled={deploying || !aiResponse}
                  className="text-green-400 border-green-500/30"
                >
                  {deploying ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Deploy & Preview
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setAiCommand(''); setAiContext(''); setAiResponse(null); setDeployResult(null); }}
                >
                  Clear
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <History className="w-4 h-4 mr-2" />
                  History
                </Button>
              </div>

              {/* Deploy Result */}
              {deployResult && (
                <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span className="font-medium text-green-400">Deploy {deployResult.status}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{deployResult.message}</p>
                  <div className="flex gap-2">
                    <a 
                      href={deployResult.preview_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-400 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Preview Site
                    </a>
                    <a 
                      href={deployResult.verification_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-400 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Verify in Admin
                    </a>
                  </div>
                </div>
              )}

              {/* Screenshot Upload */}
              <div className="mt-4 p-4 bg-secondary/30 rounded-lg border border-dashed border-border">
                <Label className="flex items-center gap-2 mb-3">
                  <Image className="w-4 h-4 text-cyan-400" />
                  Upload Screenshot for AI Analysis
                </Label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setScreenshotFile(e.target.files?.[0] || null)}
                      className="flex-1"
                    />
                    <Button
                      onClick={uploadScreenshot}
                      disabled={!screenshotFile || uploadingScreenshot}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      {uploadingScreenshot ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <Input
                    placeholder="Describe the issue in the screenshot..."
                    value={screenshotDescription}
                    onChange={(e) => setScreenshotDescription(e.target.value)}
                  />
                </div>
                
                {/* Screenshot Analysis Result */}
                {screenshotAnalysis && (
                  <div className="mt-3 p-3 bg-background/50 rounded border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-medium text-cyan-400">AI Analysis</span>
                    </div>
                    <pre className="text-xs whitespace-pre-wrap text-muted-foreground max-h-[200px] overflow-y-auto">
                      {screenshotAnalysis}
                    </pre>
                  </div>
                )}
              </div>
              
              {/* AI Response */}
              {aiResponse && (
                <div className="mt-4 p-4 bg-background/50 rounded-lg border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-medium text-purple-400">
                      AI {previewOnly ? 'Preview' : 'Response'}
                    </span>
                  </div>
                  <pre className="text-sm whitespace-pre-wrap text-muted-foreground font-mono max-h-[400px] overflow-y-auto">
                    {aiResponse}
                  </pre>
                </div>
              )}
              
              {/* Command History */}
              {showHistory && commandHistory.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Recent Commands</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {commandHistory.map((cmd, idx) => (
                      <div
                        key={idx}
                        className="p-2 bg-background/30 rounded text-xs cursor-pointer hover:bg-background/50"
                        onClick={() => setAiCommand(cmd.command)}
                      >
                        <p className="font-medium truncate">{cmd.command}</p>
                        <p className="text-muted-foreground mt-1">
                          {new Date(cmd.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Previews */}
          {previews.length > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCode className="w-5 h-5 text-cyan-400" />
                  Pending Changes ({previews.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {previews.map((preview) => (
                    <div key={preview.preview_id} className="p-4 bg-secondary/30 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{preview.file_path}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{preview.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => applyPreview(preview.preview_id)}
                            disabled={applyingPreview === preview.preview_id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {applyingPreview === preview.preview_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => rejectPreview(preview.preview_id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {preview.diff && (
                        <pre className="mt-2 p-2 bg-background/50 rounded text-xs overflow-x-auto">
                          {preview.diff}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Testing Tab */}
        <TabsContent value="testing">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="w-5 h-5 text-green-400" />
                Automated Testing
              </CardTitle>
              <CardDescription>
                Run backend tests, linting, and code validation
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => runTests('all')}
                  disabled={testLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {testLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Run All Tests
                </Button>
                <Button variant="outline" onClick={() => runTests('backend')} disabled={testLoading}>
                  Backend Only
                </Button>
                <Button variant="outline" onClick={() => runTests('lint')} disabled={testLoading}>
                  Lint Only
                </Button>
                <Button variant="outline" onClick={() => runTests('frontend')} disabled={testLoading}>
                  Frontend Lint
                </Button>
              </div>
              
              {/* Test Results */}
              {testResults && (
                <div className="p-4 bg-secondary/30 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {testResults.overall_status === 'passed' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span className="font-medium">
                        Tests {testResults.overall_status === 'passed' ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                    {testResults.overall_status !== 'passed' && (
                      <Button
                        size="sm"
                        onClick={() => fixTestFailures(testResults.test_id)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Wrench className="w-4 h-4 mr-1" />
                        Fix Issues
                      </Button>
                    )}
                  </div>
                  
                  {Object.entries(testResults.results || {}).map(([key, result]) => (
                    <div key={key} className="mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        {result.passed ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <X className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-sm font-medium capitalize">{key.replace('_', ' ')}</span>
                      </div>
                      <pre className="text-xs bg-background/50 p-2 rounded overflow-x-auto max-h-40">
                        {result.output}
                      </pre>
                    </div>
                  ))}
                  
                  {/* Fix Suggestions */}
                  {testResults.fix_suggestion && (
                    <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium text-purple-400">AI Fix Suggestions</span>
                      </div>
                      <pre className="text-xs whitespace-pre-wrap text-muted-foreground max-h-[200px] overflow-y-auto">
                        {testResults.fix_suggestion}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              
              {/* Test History */}
              {testHistory.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Recent Tests</h4>
                  <div className="space-y-2">
                    {testHistory.slice(0, 5).map((test, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-secondary/20 rounded">
                        <div className="flex items-center gap-2">
                          {test.overall_status === 'passed' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-sm">{test.test_type}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(test.timestamp).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deployments Tab */}
        <TabsContent value="deployments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-cyan-400" />
                Deployment History & Rollback
              </CardTitle>
              <CardDescription>
                View deployment history and rollback to previous versions
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {deployments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No deployments recorded</p>
                  <p className="text-sm">Changes made via AI commands will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {deployments.map((deployment) => (
                    <div key={deployment.deployment_id} className="p-4 bg-secondary/30 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={deployment.status === 'active' ? 'default' : 'secondary'}>
                              {deployment.status}
                            </Badge>
                            <span className="font-medium">{deployment.type}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {deployment.description || deployment.file_path || 'No description'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(deployment.timestamp).toLocaleString()}
                          </p>
                        </div>
                        {deployment.status === 'active' && deployment.type === 'code_change' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rollbackDeployment(deployment.deployment_id)}
                            disabled={rollbackLoading === deployment.deployment_id}
                            className="text-amber-400 border-amber-400/30"
                          >
                            {rollbackLoading === deployment.deployment_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4 mr-1" />
                            )}
                            Rollback
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Learning Tab */}
        <TabsContent value="learning">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-pink-400" />
                      AI Learning & Insights
                    </CardTitle>
                    <CardDescription>
                      Platform patterns, successful fixes, and optimization suggestions
                    </CardDescription>
                  </div>
                  <Button
                    onClick={getSuggestions}
                    disabled={suggestionsLoading}
                    className="bg-pink-600 hover:bg-pink-700"
                  >
                    {suggestionsLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Lightbulb className="w-4 h-4 mr-2" />
                    )}
                    Get AI Suggestions
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-pink-400">{insightStats.total_insights || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Insights</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-400">{insightStats.patterns || 0}</p>
                    <p className="text-xs text-muted-foreground">Patterns</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-400">{insightStats.fixes || 0}</p>
                    <p className="text-xs text-muted-foreground">Fixes Learned</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-cyan-400">{insightStats.optimizations || 0}</p>
                    <p className="text-xs text-muted-foreground">Optimizations</p>
                  </div>
                </div>
                
                {/* AI Suggestions */}
                {suggestions && (
                  <div className="mb-6 p-4 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-lg border border-pink-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-5 h-5 text-yellow-400" />
                      <span className="font-medium">AI Suggestions</span>
                    </div>
                    <pre className="text-sm whitespace-pre-wrap text-muted-foreground max-h-[300px] overflow-y-auto">
                      {suggestions}
                    </pre>
                  </div>
                )}
                
                {/* Insights List */}
                {insights.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Learned Insights</h4>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {insights.map((insight, idx) => (
                        <div key={idx} className="p-3 bg-secondary/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {insight.category}
                            </Badge>
                            <span className="text-sm font-medium">{insight.title}</span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {insight.description}
                          </p>
                          {insight.confidence > 0 && (
                            <div className="flex items-center gap-2 mt-2">
                              <TrendingUp className="w-3 h-3 text-green-400" />
                              <span className="text-xs text-muted-foreground">
                                Confidence: {(insight.confidence * 100).toFixed(0)}%
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-400" />
                Scheduled Scans
              </CardTitle>
              <CardDescription>
                Configure automatic daily scans with email reports
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {scheduleConfig && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Enable Scheduled Scans</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically scan for issues on a schedule
                      </p>
                    </div>
                    <Switch
                      checked={scheduleConfig.enabled}
                      onCheckedChange={(checked) => setScheduleConfig({...scheduleConfig, enabled: checked})}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select 
                        value={scheduleConfig.frequency} 
                        onValueChange={(v) => setScheduleConfig({...scheduleConfig, frequency: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Scan Type</Label>
                      <Select 
                        value={scheduleConfig.scan_type} 
                        onValueChange={(v) => setScheduleConfig({...scheduleConfig, scan_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full Scan</SelectItem>
                          <SelectItem value="visual">Visual Only</SelectItem>
                          <SelectItem value="code">Code Only</SelectItem>
                          <SelectItem value="data">Data Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Email Reports</Label>
                      <p className="text-sm text-muted-foreground">
                        Send scan results via email
                      </p>
                    </div>
                    <Switch
                      checked={scheduleConfig.email_report}
                      onCheckedChange={(checked) => setScheduleConfig({...scheduleConfig, email_report: checked})}
                    />
                  </div>
                  
                  {scheduleConfig.email_report && (
                    <div className="space-y-2">
                      <Label>Email Recipients (comma separated)</Label>
                      <Input
                        placeholder="admin@example.com, team@example.com"
                        value={emailRecipients}
                        onChange={(e) => setEmailRecipients(e.target.value)}
                      />
                    </div>
                  )}
                  
                  {scheduleConfig.last_run && (
                    <div className="p-3 bg-secondary/30 rounded-lg">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>Last run: {new Date(scheduleConfig.last_run).toLocaleString()}</span>
                      </div>
                      {scheduleConfig.next_run && (
                        <div className="flex items-center gap-2 text-sm mt-1">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>Next run: {new Date(scheduleConfig.next_run).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={updateSchedule}
                      disabled={scheduleLoading}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {scheduleLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Shield className="w-4 h-4 mr-2" />
                      )}
                      Save Schedule
                    </Button>
                    <Button variant="outline" onClick={runScheduledScanNow}>
                      <Play className="w-4 h-4 mr-2" />
                      Run Now
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bug Details Modal */}
      <Dialog open={showBugModal} onOpenChange={setShowBugModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedBug && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-red-400" />
                  {selectedBug.title}
                </DialogTitle>
                <DialogDescription>
                  Bug ID: {selectedBug.bug_id}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className={SEVERITY_CONFIG[selectedBug.severity]?.color}>
                    {SEVERITY_CONFIG[selectedBug.severity]?.label}
                  </Badge>
                  <Badge className={CATEGORY_CONFIG[selectedBug.category]?.color}>
                    {CATEGORY_CONFIG[selectedBug.category]?.label}
                  </Badge>
                  <Badge className={STATUS_CONFIG[selectedBug.status]?.color}>
                    {STATUS_CONFIG[selectedBug.status]?.label}
                  </Badge>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="mt-1">{selectedBug.description}</p>
                </div>
                
                {selectedBug.location && (
                  <div>
                    <Label className="text-muted-foreground">Location</Label>
                    <p className="mt-1 font-mono text-sm bg-secondary/50 px-2 py-1 rounded">
                      {selectedBug.location}
                    </p>
                  </div>
                )}
                
                {selectedBug.suggested_fix && (
                  <div>
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      AI Suggested Fix
                    </Label>
                    <pre className="mt-1 text-sm whitespace-pre-wrap bg-purple-500/10 border border-purple-500/20 p-3 rounded font-mono max-h-[200px] overflow-y-auto">
                      {selectedBug.suggested_fix}
                    </pre>
                  </div>
                )}
                
                <div className="flex items-center gap-2 pt-4 border-t">
                  <Label className="text-muted-foreground">Update Status:</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateBugStatus(selectedBug.bug_id, 'in_progress')}
                    disabled={selectedBug.status === 'in_progress'}
                  >
                    In Progress
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-green-400 border-green-500/30"
                    onClick={() => updateBugStatus(selectedBug.bug_id, 'fixed')}
                    disabled={selectedBug.status === 'fixed'}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Mark Fixed
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-gray-400"
                    onClick={() => updateBugStatus(selectedBug.bug_id, 'ignored')}
                    disabled={selectedBug.status === 'ignored'}
                  >
                    <EyeOff className="w-4 h-4 mr-1" />
                    Ignore
                  </Button>
                </div>
              </div>
              
              <DialogFooter className="flex justify-between">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteBug(selectedBug.bug_id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
                <Button
                  onClick={() => autoFixBug(selectedBug.bug_id)}
                  disabled={fixingBug === selectedBug.bug_id}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {fixingBug === selectedBug.bug_id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Auto-Fix with AI
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Bug Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report a Bug</DialogTitle>
            <DialogDescription>
              Manually report a bug or issue you've discovered
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newBug.category} onValueChange={(v) => setNewBug({ ...newBug, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visual">Visual/UI</SelectItem>
                    <SelectItem value="code">Code</SelectItem>
                    <SelectItem value="build">Build</SelectItem>
                    <SelectItem value="data">Data</SelectItem>
                    <SelectItem value="performance">Performance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select value={newBug.severity} onValueChange={(v) => setNewBug({ ...newBug, severity: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Brief description of the issue"
                value={newBug.title}
                onChange={(e) => setNewBug({ ...newBug, title: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Detailed description..."
                value={newBug.description}
                onChange={(e) => setNewBug({ ...newBug, description: e.target.value })}
                className="min-h-[100px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Location (Optional)</Label>
              <Input
                placeholder="File path, component name, or page URL"
                value={newBug.location}
                onChange={(e) => setNewBug({ ...newBug, location: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportModal(false)}>
              Cancel
            </Button>
            <Button onClick={reportBug}>
              <Bug className="w-4 h-4 mr-2" />
              Report Bug
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
